import { ExportMenu } from "@/components/ExportMenu";
import { useEffect, useState } from "react";
import api, { money, fmtDate, fmtTime, apiErr, shareSalesReportPdf } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import { useAuth } from "@/context/AuthContext";
import { useOffline } from "@/context/OfflineContext";
import { newUid } from "@/lib/deviceId";
import { useNavigate, useSearchParams } from "react-router-dom";
import Modal from "@/components/Modal";
import VoidReasonDialog from "@/components/VoidReasonDialog";
import Pagination from "@/components/Pagination";
import { InlineError } from "@/components/InlineError";
import { Wallet, Lock, Unlock, AlertTriangle, CheckCircle2, Ban, Pencil, Trash2, Loader2, Share2 } from "lucide-react";

export default function CashRegister() {
  const { current, settings } = useActivity();
  const { user } = useAuth();
  const canVoid = ["owner", "admin", "manager"].includes(user?.role) || !!user?.can_void_sales;
  const { enqueue, deviceId, bootstrap, markCashClosed, markCashOpen, offlineAction, online } = useOffline();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromPos = params.get("from") === "pos";
  const [openSessions, setOpenSessions] = useState([]);
  const [history, setHistory] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [closeModal, setCloseModal] = useState(null);
  const [openFloat, setOpenFloat] = useState("");
  const [account, setAccount] = useState("Espèces");
  const [locs, setLocs] = useState([]);
  const [openLoc, setOpenLoc] = useState("");
  const [counted, setCounted] = useState("");
  const [jDate, setJDate] = useState(new Date().toISOString().slice(0, 10));
  const [journal, setJournal] = useState(null);
  const [sessSales, setSessSales] = useState({});
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidErr, setVoidErr] = useState("");
  const [correctTarget, setCorrectTarget] = useState(null);
  const [correctLines, setCorrectLines] = useState([]);
  const [correctReason, setCorrectReason] = useState("");
  const [correctBusy, setCorrectBusy] = useState(false);
  const [correctErr, setCorrectErr] = useState("");
  const [control, setControl] = useState(null);
  const [sellerFilter, setSellerFilter] = useState("");
  const [expectedInfo, setExpectedInfo] = useState(null);
  const [histPage, setHistPage] = useState(1);
  const [journalPage, setJournalPage] = useState(1);

  const openClose = async (s) => {
    setCloseModal(s); setCounted(""); setExpectedInfo(null);
    try {
      const { data } = await api.get(`/cash/sessions/${s.id}/expected${current ? `?activity_id=${current.id}` : ""}`);
      setExpectedInfo(data);
      setCounted(String(data.expected));
    } catch {
      // hors-ligne ou indisponible : pré-remplir avec le fonds initial connu
      if (s.opening_float != null) setCounted(String(s.opening_float));
    }
  };

  const loadSessionSales = async (sessions) => {
    if (!canVoid) return;
    const entries = await Promise.all((sessions || []).map(async (s) => {
      try { const { data } = await api.get(`/cash/sessions/${s.id}/sales${current ? `?activity_id=${current.id}` : ""}`); return [s.id, data]; }
      catch { return [s.id, []]; }
    }));
    setSessSales(Object.fromEntries(entries));
    try { const { data } = await api.get(current ? `/cash/control?activity_id=${current.id}` : "/cash/control"); setControl(data); } catch { /* non-privileged */ }
  };
  const doVoidSessionSale = async (reason) => {
    if (!voidTarget) return;
    setVoidBusy(true); setVoidErr("");
    try { await api.post(`/sales/${voidTarget.id}/void${current ? `?activity_id=${current.id}` : ""}`, { reason }); toast.success(`Vente ${voidTarget.number} annulée`); setVoidTarget(null); load(); }
    catch (e) { setVoidErr(apiErr(e.response?.data?.detail)); }
    finally { setVoidBusy(false); }
  };
  const openCorrect = (sale) => {
    setCorrectErr(""); setCorrectReason("");
    setCorrectLines((sale.lines || []).map((l) => ({ product_id: l.product_id, product_name: l.product_name, packaging: l.packaging, quantity: l.quantity, unit_price: l.unit_price, discount: l.discount || 0 })));
    setCorrectTarget(sale);
  };
  const setCorrectLine = (i, field, val) => setCorrectLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
  const removeCorrectLine = (i) => setCorrectLines((ls) => ls.filter((_, idx) => idx !== i));
  const saveCorrect = async () => {
    if (!correctReason.trim()) { setCorrectErr("Le motif de la correction est obligatoire."); return; }
    if (correctLines.length === 0) { setCorrectErr("Ajoutez au moins une ligne (ou annulez plutôt la vente)."); return; }
    setCorrectBusy(true); setCorrectErr("");
    try {
      const lines = correctLines.map((l) => ({ product_id: l.product_id, packaging: l.packaging, quantity: Number(l.quantity), unit_price: Number(l.unit_price), discount: Number(l.discount || 0) }));
      await api.post(`/sales/${correctTarget.id}/correct${current ? `?activity_id=${current.id}` : ""}`, { reason: correctReason.trim(), lines });
      toast.success("Vente corrigée (annulée puis ressaisie)");
      setCorrectTarget(null); load();
    } catch (e) { setCorrectErr(apiErr(e.response?.data?.detail)); }
    finally { setCorrectBusy(false); }
  };

  const load = async () => {
    if (!navigator.onLine) {
      const cs = bootstrap?.cash_session;
      setOpenSessions(cs ? [cs] : []);
      setHistory([]);
      return;
    }
    try {
      const [o, h] = await Promise.all([
        api.get(current ? `/cash/current?activity_id=${current.id}` : "/cash/current"),
        api.get(current ? `/cash/sessions?activity_id=${current.id}` : "/cash/sessions"),
      ]);
      setOpenSessions(o.data); setHistory(h.data);
      loadSessionSales(o.data);
    } catch {
      const cs = bootstrap?.cash_session;
      setOpenSessions(cs ? [cs] : []);
    }
  };
  // Changement d'activité : on vide immédiatement l'état de l'activité précédente puis on recharge.
  useEffect(() => {
    setOpenSessions([]); setHistory([]); setSessSales({}); setControl(null); setExpectedInfo(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrap, current?.id]);

  const restrictedLoc = !!(user && !(user.is_owner || ["admin", "manager"].includes(user.role)) && (user.location_ids?.length > 0));
  const canOpenCash = !!(user && (user.is_owner || ["admin", "manager", "cashier"].includes(user.role)));
  // Chacun gère SA propre caisse : on désactive « Ouvrir une caisse » uniquement si
  // l'utilisateur courant a DÉJÀ sa propre session ouverte dans cette activité.
  const hasMyOpenCash = openSessions.some((s) =>
    String(s.opened_by_id) === String(user?.id) && (!current || !s.activity_id || s.activity_id === current.id));

  useEffect(() => {
    const q = `date=${jDate}${current ? `&activity_id=${current.id}` : ""}`;
    api.get(`/cash/journal?${q}`).then((r) => { setJournal(r.data); setJournalPage(1); }).catch(() => {});
  }, [jDate, current]);

  useEffect(() => {
    if (!current) return;
    if (!navigator.onLine) {
      const bl = bootstrap?.location ? [bootstrap.location] : [];
      setLocs(bl); setOpenLoc((p) => p || params.get("loc") || bl[0]?.id || "");
      return;
    }
    api.get(`/locations?activity_id=${current.id}`).then((r) => {
      const rows = r.data;
      setLocs(rows);
      const allowed = restrictedLoc ? rows.filter((l) => (user?.location_ids || []).includes(l.id)) : rows;
      // Par défaut on ouvre la caisse au POINT DE VENTE (comme le POS), pas l'entrepôt,
      // pour que la session soit rattachée au même emplacement et visible dans le POS.
      const pref = allowed.find((l) => l.type !== "warehouse") || allowed[0];
      setOpenLoc((p) => p || params.get("loc") || pref?.id || rows[0]?.id || "");
    }).catch(() => {});
  }, [current, bootstrap]);

  // When launched from the POS, open the "open cash" modal automatically
  useEffect(() => { if (fromPos) setOpenModal(true); }, [fromPos]);

  const doOpen = async () => {
    if (!current) return;
    if (!navigator.onLine) {
      const loc = locs.find((l) => l.id === openLoc);
      const tmpId = "tmp-" + newUid();
      const session = { id: tmpId, activity_id: current.id, account, location_id: openLoc || null, location_name: loc?.name || "", opening_float: Number(openFloat || 0), status: "open", opened_by: user?.name || "", opened_by_id: user?.id || "", opened_at: new Date().toISOString(), offline: true };
      await offlineAction({ uid: newUid(), type: "cash_open", providesId: tmpId, method: "post", url: "/cash/sessions/open", payload: { activity_id: current.id, account, opening_float: Number(openFloat || 0), location_id: openLoc || null } });
      await markCashOpen(session);
      toast.success("Caisse ouverte hors-ligne (en attente de synchronisation)");
      setOpenModal(false); setOpenFloat("");
      setOpenSessions([session]);
      if (fromPos) { navigate("/pos"); return; }
      return;
    }
    try {
      await api.post("/cash/sessions/open", { activity_id: current.id, account, opening_float: Number(openFloat || 0), location_id: openLoc || null });
      toast.success("Caisse ouverte"); setOpenModal(false); setOpenFloat("");
      if (fromPos) { navigate("/pos"); return; }  // return automatically to the POS
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const doClose = async () => {
    const payload = { counted: Number(counted || 0), client_uid: newUid(), device_id: deviceId };
    if (!navigator.onLine) {
      await enqueue({ uid: payload.client_uid, type: "cash_close", session_id: closeModal.id, payload, localSale: { number: "Clôture caisse", total: payload.counted } });
      toast.success("Clôture enregistrée hors-ligne (en attente de synchronisation)");
      setCloseModal(null); setCounted("");
      setOpenSessions((s) => s.filter((x) => x.id !== closeModal.id));
      await markCashClosed();
      return;
    }
    try {
      const { data } = await api.post(`/cash/sessions/${closeModal.id}/close${current ? `?activity_id=${current.id}` : ""}`, payload);
      if (data.alert) toast.warning(data.alert); else toast.success("Caisse clôturée sans écart");
      setCloseModal(null); setCounted(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Caisse</h1>
          <p className="text-sm text-[#64748B] mt-1">Ouverture, clôture et rapprochement</p></div>
        <div className="flex flex-wrap gap-2 items-center">
          <ExportMenu dataset="cash" params={{ activity_id: current?.id }} />
          {canOpenCash ? (
            hasMyOpenCash ? (
              <div className="flex items-center gap-2">
                <button disabled title="Une caisse est déjà ouverte" className="btn-primary opacity-50 cursor-not-allowed pointer-events-none" data-testid="open-cash-btn" aria-disabled="true"><Unlock className="w-4 h-4" /> Ouvrir une caisse</button>
                <span className="inline-flex items-center gap-1 rounded-full border border-[#bbf7d0] bg-[#DCFCE7] text-[#15803D] px-2.5 py-1 text-xs font-semibold whitespace-nowrap" data-testid="cash-status-indicator"><Unlock className="w-3 h-3" /> Caisse ouverte</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button onClick={() => setOpenModal(true)} className="btn-primary" data-testid="open-cash-btn"><Unlock className="w-4 h-4" /> Ouvrir une caisse</button>
                <span className="inline-flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-[#F1F5F9] text-[#64748B] px-2.5 py-1 text-xs font-semibold whitespace-nowrap" data-testid="cash-status-indicator"><Lock className="w-3 h-3" /> Caisse fermée</span>
              </div>
            )
          ) : (
            <span className="text-sm text-[#94A3B8]" data-testid="cash-no-permission">Vous n'avez pas l'autorisation d'ouvrir une caisse</span>
          )}
        </div>
      </div>

      {control && control.by_seller && (
        <div className="card p-5" data-testid="cash-control">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-lg font-semibold font-head text-[#16324F]">Contrôle de caisse — aujourd'hui</h3>
            <select className="inp !py-1.5 w-auto" value={sellerFilter} onChange={(e) => setSellerFilter(e.target.value)} data-testid="cash-control-seller-filter">
              <option value="">Tous les vendeurs</option>
              {control.by_seller.map((r) => <option key={r.seller_id} value={r.seller_id}>{r.seller_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            <div className="p-3 rounded-lg bg-[#F0F9FF] border border-[#bae6fd]"><div className="text-xs text-[#64748B]">Ventes du jour</div><div className="text-lg font-bold text-[#16324F]" data-testid="cash-control-total-sales">{money((sellerFilter ? control.by_seller.filter((r) => r.seller_id === sellerFilter) : control.by_seller).reduce((a, r) => a + r.sales_amount, 0))}</div></div>
            <div className="p-3 rounded-lg bg-[#F0FDF4] border border-[#bbf7d0]"><div className="text-xs text-[#64748B]">Encaissé</div><div className="text-lg font-bold text-[#15803D]" data-testid="cash-control-total-received">{money((sellerFilter ? control.by_seller.filter((r) => r.seller_id === sellerFilter) : control.by_seller).reduce((a, r) => a + r.received, 0))}</div></div>
            <div className="p-3 rounded-lg bg-[#FAFAFA] border border-[#E2E8F0]"><div className="text-xs text-[#64748B]">Nb ventes</div><div className="text-lg font-bold text-[#16324F]">{(sellerFilter ? control.by_seller.filter((r) => r.seller_id === sellerFilter) : control.by_seller).reduce((a, r) => a + r.sales_count, 0)}</div></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm"><thead><tr><th className="th">Vendeur</th><th className="th text-right">Ventes</th><th className="th text-right">Montant</th><th className="th text-right">Encaissé</th></tr></thead>
              <tbody>
                {(sellerFilter ? control.by_seller.filter((r) => r.seller_id === sellerFilter) : control.by_seller).map((r) => (
                  <tr key={r.seller_id} className="row" data-testid={`cash-control-row-${r.seller_id}`}><td className="td font-medium">{r.seller_name}</td><td className="td text-right">{r.sales_count}</td><td className="td text-right font-semibold">{money(r.sales_amount)}</td><td className="td text-right text-[#15803D]">{money(r.received)}</td></tr>
                ))}
                {control.by_seller.length === 0 && <tr><td colSpan={4} className="td text-center text-[#94A3B8] py-6 whitespace-normal">Aucune vente aujourd'hui</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end">
            <button className="btn-success !py-2" data-testid="cashier-report-pdf-btn"
              onClick={() => shareSalesReportPdf({ activityId: current?.id, days: 1, sellerId: sellerFilter, orgName: settings?.org_name, subtitle: current?.name, sellerLabel: sellerFilter ? (control.by_seller.find((r) => r.seller_id === sellerFilter)?.seller_name || "Caissier") : "Tous" })}>
              <Share2 className="w-4 h-4" /> Rapport du jour (PDF / WhatsApp)
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {openSessions.map((s) => (
          <div key={s.id} className="card p-5" data-testid={`open-session-${s.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><div className="w-10 h-10 rounded-lg bg-[#DCFCE7] text-[#15803D] flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
                <div><div className="font-semibold text-[#16324F]">{s.account}{s.location_name ? ` · ${s.location_name}` : ""}</div><div className="text-xs text-[#64748B]">Ouverte par {s.opened_by} · {fmtDate(s.opened_at)}</div></div></div>
              <span className="inline-flex items-center gap-1 rounded-full border border-[#bbf7d0] bg-[#DCFCE7] text-[#15803D] px-2.5 py-0.5 text-xs font-semibold"><Unlock className="w-3 h-3" /> Ouverte</span>
            </div>
            {canVoid && (
              <div className="mt-4 border-t border-[#E2E8F0] pt-3">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-2">Ventes de cette caisse ({(sessSales[s.id] || []).length}) — corrigez avant de clôturer</div>
                <div className="max-h-56 overflow-y-auto space-y-1.5">
                  {(sessSales[s.id] || []).map((sale) => (
                    <div key={sale.id} className="flex items-center justify-between gap-2 text-sm p-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]" data-testid={`sess-sale-${sale.id}`}>
                      <div className="min-w-0">
                        <div className="font-mono text-[11px] text-[#64748B]">{sale.number} · {fmtTime(sale.created_at)}</div>
                        <div className="truncate text-[#0F172A]">{sale.customer_name || "Comptant"}{sale.voided && <span className="ml-1 text-[#B91C1C] font-semibold">· Annulée{sale.voided_by ? ` par ${sale.voided_by}` : ""}</span>}{sale.corrected_from && <span className="ml-1 text-[#0E7490] text-xs">(corrigée)</span>}</div>
                        {sale.voided && (sale.voided_at || sale.voided_reason) && <div className="text-[10px] text-[#94A3B8] leading-tight mt-0.5">{sale.voided_at ? fmtDate(sale.voided_at) : ""}{sale.voided_reason ? ` · ${sale.voided_reason}` : ""}</div>}
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`font-semibold ${sale.voided ? "line-through text-[#94A3B8]" : ""}`}>{money(sale.total)}</div>
                        {!sale.voided && (
                          <div className="flex gap-1.5 mt-1.5 justify-end">
                            <button onClick={() => openCorrect(sale)} className="btn-outline !py-1.5 !px-3" data-testid={`sess-correct-${sale.id}`}><Pencil className="w-3.5 h-3.5" /> Corriger</button>
                            <button onClick={() => { setVoidErr(""); setVoidTarget(sale); }} className="btn-remove !py-1.5 !px-3" data-testid={`sess-void-${sale.id}`}><Ban className="w-3.5 h-3.5" /> Annuler</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {(sessSales[s.id] || []).length === 0 && <div className="text-xs text-[#94A3B8] py-3 text-center">Aucune vente pour l'instant</div>}
                </div>
              </div>
            )}
            <div className="mt-4 flex items-center justify-between border-t border-[#E2E8F0] pt-3">
              <div><div className="kpi-label">Fonds initial</div><div className="text-xl font-bold font-head">{money(s.opening_float)}</div></div>
              <div className="flex items-center gap-2">
                <button onClick={() => shareSalesReportPdf({ activityId: current?.id, days: 1, sellerId: user.id, orgName: settings?.org_name, subtitle: current?.name, sellerLabel: user.name })} className="btn-outline !text-[#15803D]" data-testid={`session-report-pdf-${s.id}`}><Share2 className="w-4 h-4" /> Rapport</button>
                <button onClick={() => openClose(s)} className="btn-navy" data-testid={`close-cash-btn-${s.id}`}><Lock className="w-4 h-4" /> Clôturer</button>
              </div>
            </div>
          </div>
        ))}
        {openSessions.length === 0 && <div className="card p-8 text-center text-[#94A3B8] md:col-span-2">Aucune caisse ouverte</div>}
      </div>

      <div>
        <h3 className="text-lg font-semibold font-head text-[#16324F] mb-3">Historique des clôtures {!online && <span className="text-xs font-normal text-[#B45309]">(indisponible hors connexion)</span>}</h3>
        <div className="card">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full"><thead><tr>
              <th className="th">Compte</th><th className="th">Ouverte</th><th className="th">Clôturée</th>
              <th className="th text-right">Attendu</th><th className="th text-right">Compté</th><th className="th text-right">Écart</th>
            </tr></thead><tbody>
              {history.filter((s) => s.status === "closed").slice((histPage - 1) * 10, histPage * 10).map((s) => (
                <tr key={s.id} className="row">
                  <td className="td font-medium">{s.account}</td>
                  <td className="td text-xs">{fmtDate(s.opened_at)}</td>
                  <td className="td text-xs">{fmtDate(s.closed_at)}</td>
                  <td className="td text-right">{money(s.expected)}</td>
                  <td className="td text-right">{money(s.counted)}</td>
                  <td className={`td text-right font-semibold flex items-center justify-end gap-1 ${Math.round(s.discrepancy || 0) === 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>
                    {Math.round(s.discrepancy || 0) === 0 ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}{money(s.discrepancy)}</td>
                </tr>
              ))}
              {history.filter((s) => s.status === "closed").length === 0 && <tr><td colSpan={6} className="td text-center py-8 text-[#94A3B8]">Aucune clôture</td></tr>}
            </tbody></table>
          </div>
          <div className="md:hidden divide-y divide-[#E2E8F0]">
            {history.filter((s) => s.status === "closed").slice((histPage - 1) * 10, histPage * 10).map((s) => (
              <div key={s.id} className="py-4 first:pt-1" data-testid={`closure-card-${s.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-[#16324F]">{s.account}</span>
                  <span className={`inline-flex items-center gap-1 text-sm font-semibold ${Math.round(s.discrepancy || 0) === 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>
                    {Math.round(s.discrepancy || 0) === 0 ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}Écart {money(s.discrepancy)}</span>
                </div>
                <div className="text-xs text-[#94A3B8] mt-0.5">{fmtDate(s.opened_at)} → {fmtDate(s.closed_at)}</div>
                <div className="mt-2 flex items-center gap-4 text-xs text-[#64748B]">
                  <span>Attendu <span className="font-medium text-[#334155]">{money(s.expected)}</span></span>
                  <span>Compté <span className="font-medium text-[#334155]">{money(s.counted)}</span></span>
                </div>
              </div>
            ))}
            {history.filter((s) => s.status === "closed").length === 0 && <div className="text-center py-8 text-[#94A3B8]">Aucune clôture</div>}
          </div>
          <Pagination page={histPage} setPage={setHistPage} total={history.filter((s) => s.status === "closed").length} />
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-lg font-semibold font-head text-[#16324F]">Journal de caisse (espèces) {!online && <span className="text-xs font-normal text-[#B45309]">(indisponible hors connexion)</span>}</h3>
          <input type="date" className="inp !w-auto" value={jDate} onChange={(e) => setJDate(e.target.value)} data-testid="cash-journal-date" />
        </div>
        {journal && (
          <div className="card">
            <div className="flex flex-wrap gap-5 p-4 border-b border-[#E2E8F0] text-sm">
              <span>Entrées : <b className="text-[#15803D]">{money(journal.total_in)}</b></span>
              <span>Sorties : <b className="text-[#B91C1C]">{money(journal.total_out)}</b></span>
              <span>Solde du jour : <b className="text-[#16324F]">{money(journal.balance)}</b></span>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full"><thead><tr>
                <th className="th">Heure</th><th className="th">Opération</th><th className="th text-right">Montant</th><th className="th text-right">Solde</th>
              </tr></thead><tbody>
                {journal.entries.slice((journalPage - 1) * 10, journalPage * 10).map((en, i) => (
                  <tr key={i} className="row" data-testid={`journal-row-${i}`}>
                    <td className="td text-xs">{fmtDate(en.time)}</td>
                    <td className="td">{en.label}</td>
                    <td className={`td text-right font-semibold ${en.amount >= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>{en.amount >= 0 ? "+" : ""}{money(en.amount)}</td>
                    <td className="td text-right">{money(en.balance)}</td>
                  </tr>
                ))}
                {journal.entries.length === 0 && <tr><td colSpan={4} className="td text-center py-8 text-[#94A3B8]">Aucun mouvement d'espèces ce jour</td></tr>}
              </tbody></table>
            </div>
            <div className="md:hidden divide-y divide-[#E2E8F0] px-4">
              {journal.entries.slice((journalPage - 1) * 10, journalPage * 10).map((en, i) => (
                <div key={i} className="py-3 flex items-center justify-between gap-3" data-testid={`journal-card-${i}`}>
                  <div className="min-w-0">
                    <div className="text-[#0F172A] truncate">{en.label}</div>
                    <div className="text-xs text-[#94A3B8] mt-0.5">{fmtDate(en.time)}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-semibold ${en.amount >= 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>{en.amount >= 0 ? "+" : ""}{money(en.amount)}</div>
                    <div className="text-xs text-[#94A3B8]">Solde {money(en.balance)}</div>
                  </div>
                </div>
              ))}
              {journal.entries.length === 0 && <div className="text-center py-8 text-[#94A3B8]">Aucun mouvement d'espèces ce jour</div>}
            </div>
            <div className="px-4 pb-3"><Pagination page={journalPage} setPage={setJournalPage} total={journal.entries.length} /></div>
          </div>
        )}
      </div>

      <Modal open={openModal} onClose={() => setOpenModal(false)} title="Ouvrir une caisse" testId="open-cash-modal"
        footer={<><button className="btn-outline" onClick={() => setOpenModal(false)}>Annuler</button><button className="btn-primary" onClick={doOpen} data-testid="confirm-open-cash-btn">Ouvrir</button></>}>
        <div className="space-y-4">
          {(() => {
            let allowedLocs = restrictedLoc ? locs.filter((l) => (user?.location_ids || []).includes(l.id)) : locs;
            // Propriétaire/admin : peut ouvrir une caisse à l'entrepôt (vente en gros), mais
            // seulement en mode entrepôt séparé. En mono-boutique, jamais l'entrepôt partagé du
            // tenant. Repli : si l'activité n'a QUE l'entrepôt, on le garde.
            const privileged = user && (user.is_owner || user.role === "admin") && current?.stock_mode === "advanced";
            if (!privileged) {
              const nonWh = allowedLocs.filter((l) => l.type !== "warehouse");
              if (nonWh.length) allowedLocs = nonWh;
            }
            return allowedLocs.length > 1 ? (
            <div><label className="lbl">Point de vente</label>
            <select className="inp" value={openLoc} onChange={(e) => setOpenLoc(e.target.value)} data-testid="cash-location-select">
              {allowedLocs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select></div>
          ) : (
            <div><label className="lbl">Point de vente</label>
            <div className="inp bg-[#F8FAFC] flex items-center" data-testid="cash-location-fixed">{allowedLocs[0]?.name || locs.find((l) => l.id === openLoc)?.name || "Emplacement par défaut"}</div></div>
          ); })()}
          <div><label className="lbl">Compte</label>
            <select className="inp" value={account} onChange={(e) => setAccount(e.target.value)}><option>Espèces</option><option>Mobile Money</option><option>Banque</option></select></div>
          <div><label className="lbl">Fonds initial compté</label><input type="number" className="inp" value={openFloat} onChange={(e) => setOpenFloat(e.target.value)} data-testid="opening-float-input" /></div>
        </div>
      </Modal>

      <Modal open={!!closeModal} onClose={() => { setCloseModal(null); setExpectedInfo(null); setCounted(""); }} title="Clôturer la caisse" testId="close-cash-modal"
        footer={<><button className="btn-outline" onClick={() => { setCloseModal(null); setExpectedInfo(null); setCounted(""); }}>Annuler</button><button className="btn-navy" onClick={doClose} data-testid="confirm-close-cash-btn">Clôturer</button></>}>
        <div className="space-y-4">
          {expectedInfo ? (
            <div className="rounded-xl border border-[#DBEAFE] bg-[#F0F9FF] p-3.5" data-testid="close-expected-info">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#64748B]">Attendu en caisse</span>
                <span className="text-xl font-bold font-head text-[#16324F]" data-testid="close-expected-amount">{money(expectedInfo.expected)}</span>
              </div>
              <div className="text-[11px] text-[#64748B] mt-1.5 leading-relaxed">Fonds initial {money(expectedInfo.opening_float)} + encaissements espèces {money(expectedInfo.cash_in)} − sorties espèces {money(expectedInfo.cash_out)}</div>
            </div>
          ) : (
            <p className="text-sm text-[#B45309]">Montant attendu indisponible (hors-ligne). Saisissez le montant réellement compté.</p>
          )}
          <p className="text-sm text-[#64748B]">Le montant compté est pré-rempli avec l'attendu pour aller plus vite. Comptez les espèces et corrigez-le si nécessaire — l'écart sera calculé et audité.</p>
          <div>
            <label className="lbl">Montant réel compté</label>
            <input type="number" className="inp" value={counted} onChange={(e) => setCounted(e.target.value)} data-testid="counted-input" />
            {expectedInfo && counted !== "" && Number(counted) !== expectedInfo.expected && (
              <div className={`text-xs font-semibold mt-1.5 ${Number(counted) - expectedInfo.expected < 0 ? "text-[#B91C1C]" : "text-[#B45309]"}`} data-testid="close-discrepancy-preview">
                Écart : {Number(counted) - expectedInfo.expected > 0 ? "+" : ""}{money(Number(counted) - expectedInfo.expected)}
              </div>
            )}
          </div>
        </div>
      </Modal>
      <VoidReasonDialog open={!!voidTarget} title="Annuler la vente"
        message={`Annuler la vente ${voidTarget?.number || ""} de ${money(voidTarget?.total || 0)} ? Le stock, la caisse et la créance éventuelle seront rétablis. La vente restera visible avec le statut « Annulée ».`}
        confirmLabel="Annuler la vente" loading={voidBusy} error={voidErr}
        onConfirm={doVoidSessionSale} onClose={() => setVoidTarget(null)} testId="cash-void-dialog" />

      <Modal open={!!correctTarget} onClose={() => setCorrectTarget(null)} title={`Corriger — ${correctTarget?.number || ""}`} wide testId="correct-sale-modal"
        footer={<><button className="btn-outline" onClick={() => setCorrectTarget(null)}>Annuler</button><button className="btn-primary" onClick={saveCorrect} disabled={correctBusy} data-testid="correct-save-btn">{correctBusy && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer la correction</button></>}>
        <div className="space-y-3">
          <p className="text-sm text-[#64748B]">La vente sera annulée puis ressaisie avec ces valeurs. Le stock, la caisse et la créance sont recalculés automatiquement — rien n'est effacé, tout reste tracé.</p>
          {correctLines.map((ln, i) => (
            <div key={i} className="p-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC]" data-testid={`correct-line-${i}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-[#0F172A]">{ln.product_name}<span className="text-[#64748B]"> · {ln.packaging === "base" ? "à l'unité" : ln.packaging}</span></div>
                <button onClick={() => removeCorrectLine(i)} className="text-[#B91C1C]" data-testid={`correct-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div><label className="lbl">Quantité</label><input type="number" min="1" className="inp" value={ln.quantity} onChange={(e) => setCorrectLine(i, "quantity", e.target.value)} data-testid={`correct-qty-${i}`} /></div>
                <div><label className="lbl">Prix unitaire</label><input type="number" className="inp" value={ln.unit_price} onChange={(e) => setCorrectLine(i, "unit_price", e.target.value)} data-testid={`correct-price-${i}`} /></div>
              </div>
            </div>
          ))}
          {correctLines.length === 0 && <p className="text-xs text-[#B91C1C]">Plus aucune ligne — annulez plutôt la vente.</p>}
          <div><label className="lbl">Motif de la correction <span className="text-[#B91C1C]">*</span></label>
            <textarea rows={2} className="inp" value={correctReason} onChange={(e) => setCorrectReason(e.target.value)} placeholder="Ex. quantité saisie 8 au lieu de 5" data-testid="correct-reason-input" /></div>
          <InlineError message={correctErr} />
        </div>
      </Modal>
    </div>
  );
}
